import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { RequirePermission } from "@components/common/require-permission";
import { BaseCompanyProps } from "@custom-types/base-types";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
import { Box, Step, StepContent, StepLabel, Stepper } from "@mui-wrapper";
import { useAppNavigation } from "@router/use-app-navigation";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { Button, Dialog, Text } from "@flat-ui";
import { deleteWorkspaceSteps } from "@pages/workspace-profile/tab-contents/workspace-info-tab/delete-workspace/delete-workspace-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { TextWithTooltip } from "@components/common/text-with-tooltip";

/** Types of dialogs that can be opened */
type OpenDialog = "deleteWorkspace" | "success" | "confirmation" | null;

type DeleteWorkspaceRequestType =
  /** Gets the summary of the workspace */
  | "Get Summary"

  /** Gets the token for workspace deletion */
  | "Get Token"

  /** Deletes the workspace */
  | "Delete Workspace";

/** Component for deleting workspace */
export function DeleteWorkspace({
  company,
}: BaseCompanyProps): React.JSX.Element {
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const [activeStepper, setActiveStepper] = useState<number>(0);
  const [shouldDisableContinueButton, setShouldDisableContinueButton] =
    useState<boolean>(false);
  const [deleteWorkspaceResponse, setDeleteWorkspaceResponse] =
    useState<FaroCoreApiTypes.DeleteCompanyResponse>();
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { navigateToHomePage } = useAppNavigation();

  function onCloseDialogs(): void {
    setOpenDialog(null);
    setActiveStepper(0);
    setDeleteWorkspaceResponse(undefined);
  }

  const steps = useMemo(
    () =>
      deleteWorkspaceSteps({
        company,
        setShouldDisableContinueButton,
        deleteWorkspaceResponse,
      }),
    [company, deleteWorkspaceResponse]
  );

  async function onDeleteWorkspace(
    type: DeleteWorkspaceRequestType
  ): Promise<void> {
    trackEvent({
      name: WorkspaceEvents.deleteWorkspace,
    });

    const token =
      type === "Delete Workspace" &&
      deleteWorkspaceResponse &&
      "token" in deleteWorkspaceResponse
        ? deleteWorkspaceResponse.token
        : undefined;

    setIsFetching(true);
    try {
      const response = await faroCoreApiClient.admin.deleteWorkspace({
        companyId: company.id,
        token,
      });

      setDeleteWorkspaceResponse(response);
      if (type === "Delete Workspace" && response.status === "SUCCESS") {
        setOpenDialog("success");
      }
    } catch (error) {
      handleErrorWithToast({
        id: `delete-workspace-${Date.now().toString()}`,
        title: "Could not delete workspace",
        error,
      });
    } finally {
      setIsFetching(false);
    }
  }

  const isLastStep = activeStepper === steps.length - 1;

  return (
    <Box>
      <RequirePermission permission="workspace.deleteWorkspace">
        <Button
          variant="destructiveGhost"
          onClick={() => {
            void onDeleteWorkspace("Get Summary");
            setOpenDialog("deleteWorkspace");
          }}
        >
          Delete Workspace
        </Button>
      </RequirePermission>

      <Dialog
        open={openDialog === "deleteWorkspace"}
        title={
          <TextWithTooltip
            prefix="Are you sure you want to delete"
            text={company.name}
            suffix="workspace?"
          />
        }
        onCancel={() => {
          activeStepper === 0
            ? onCloseDialogs()
            : setActiveStepper((prevActiveStep) => prevActiveStep - 1);
        }}
        cancelText={activeStepper === 0 ? "Cancel" : "Back"}
        onConfirm={() => {
          if (isLastStep) {
            void onDeleteWorkspace("Get Token");
            setOpenDialog("confirmation");
          } else {
            setActiveStepper((prevActiveStep) => prevActiveStep + 1);
          }
        }}
        isConfirmDisabled={shouldDisableContinueButton}
        confirmText={isLastStep ? "Delete Workspace" : "Continue"}
        showSpinner={isFetching}
      >
        <Stepper activeStep={activeStepper} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={`step${index}`}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>{step.content}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Dialog>

      <Dialog
        open={openDialog === "confirmation"}
        title="Confirm workspace deletion"
        onConfirm={() => onDeleteWorkspace("Delete Workspace")}
        onCancel={() => {
          setOpenDialog("deleteWorkspace");
          setDeleteWorkspaceResponse(undefined);
        }}
        showSpinner={isFetching}
      >
        <TextWithTooltip
          prefix="Are you sure you want to delete"
          text={company.name}
          suffix="workspace? This action is irreversible"
        />
      </Dialog>

      <Dialog
        open={openDialog === "success"}
        title="Workspace Deleted"
        onConfirm={() => {
          onCloseDialogs();
          navigateToHomePage();
        }}
        confirmText="Close"
      >
        <Text variant="bodyL">
          The workspace has been successfully deleted from the FARO ecosystem.
          Please click on close to navigate to home page.
        </Text>
      </Dialog>
    </Box>
  );
}
